import { useState } from 'react';
import { useTonWallet } from '../hooks/useTonWallet';
import type { Cell } from '../demo/seed';
import { fmtTon } from '../lib/format';

type Props = {
  cell: Cell;
  onStaked: (amount: number) => void;
};

export function StakeForm({ cell, onStaked }: Props) {
  const { isConnected, stake, connect } = useTonWallet();
  const [amount, setAmount] = useState('1');
  const [status, setStatus] = useState<'idle' | 'pending' | 'ok' | 'error'>('idle');
  const [myStake, setMyStake] = useState(0);
  const [error, setError] = useState<string | null>(null);

  if (!cell.agent) {
    return (
      <div className="text-xs text-text-mono-dim">Cell is empty. Nothing to stake on.</div>
    );
  }

  async function handleStake() {
    setError(null);
    if (!isConnected) {
      connect();
      return;
    }
    const n = parseFloat(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Enter a valid TON amount.');
      return;
    }
    try {
      setStatus('pending');
      await stake(cell.id, cell.agent!.walletAddress, n);
      setStatus('ok');
      setMyStake((s) => s + n);
      onStaked(n);
    } catch (e: any) {
      setStatus('error');
      setError(e?.message ?? 'Transaction failed.');
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="number"
          step="0.1"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="hairline bg-void px-2 py-1 w-24 text-md tabular"
          aria-label="Stake amount in TON"
        />
        <span className="text-text-mono-dim text-xs">TON</span>
        <button
          onClick={handleStake}
          disabled={status === 'pending'}
          className="ml-auto px-3 py-1 bg-blue-life text-void text-xs uppercase tracking-tightest rounded-sm hover:bg-blue-glow disabled:opacity-50"
        >
          {status === 'pending' ? 'Sending…' : isConnected ? 'Stake' : 'Connect to stake'}
        </button>
      </div>
      <div className="text-2xs text-text-secondary flex justify-between">
        <span>Your stake: ◊ {fmtTon(myStake)} TON</span>
        <span>Pro-rata claim on future earnings</span>
      </div>
      {error && <div className="text-2xs text-red-critical">{error}</div>}
      {status === 'ok' && (
        <div className="text-2xs text-green-thrive">✓ Transaction sent</div>
      )}
    </div>
  );
}
